// 📁 src/ProductsPage.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";
import { CurrencyContext } from "./context/CurrencyContext";

export default function ProductsPage() {
  const location = useLocation();
  const { cart, addToCart, increaseQty, decreaseQty } = useContext(CartContext);
  const { currency, rates } = useContext(CurrencyContext);

  // ✅ IMPORTANT: In production (Vercel) this MUST be set in env:
  // REACT_APP_API_URL = https://your-backend-domain.com
  const API_BASE = useMemo(() => {
    const raw = process.env.REACT_APP_API_URL || "http://localhost:5000";
    return raw.replace(/\/$/, ""); // remove trailing slash
  }, []);

  const [productsByCategory, setProductsByCategory] = useState({
    raw: [],
    roasted: [],
    flavored: [],
    giftpacks: [],
  });

  const [loading, setLoading] = useState(true);

  const symbolMap = {
    INR: "₹",
    USD: "$",
    EUR: "€",
    GBP: "£",
    AED: "د.إ",
  };

  const fallbackRates = {
    INR: 1,
    USD: 0.012,
    EUR: 0.011,
    GBP: 0.0098,
    AED: 0.044,
  };

  const rate = rates?.[currency] || fallbackRates[currency];

  // ✅ Fetch products from backend (works on Vercel when REACT_APP_API_URL is set)
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        const res = await fetch(`${API_BASE}/api/products`, { cache: "no-store" });
        if (!res.ok) {
          throw new Error(`Failed to fetch products: ${res.status} ${res.statusText}`);
        }

        const data = await res.json();
        const grouped = { raw: [], roasted: [], flavored: [], giftpacks: [] };

        (Array.isArray(data) ? data : []).forEach((p) => {
          const rawCategory = p.category?.toLowerCase?.().trim?.() || "";

          if (rawCategory === "raw") grouped.raw.push(p);
          else if (rawCategory === "roasted") grouped.roasted.push(p);
          else if (rawCategory === "flavored") grouped.flavored.push(p);
          else if (rawCategory.includes("gift")) grouped.giftpacks.push(p);
        });

        setProductsByCategory(grouped);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [API_BASE]);

  // ✅ Scroll to hash section after products load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const timeout = setTimeout(() => {
        const section = document.getElementById(id);
        if (section) section.scrollIntoView({ behavior: "smooth" });
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [location, productsByCategory]);

  const getQtyInCart = (product) => {
    if (!Array.isArray(cart)) return 0;

    const match =
      cart.find(
        (i) =>
          i._id === product._id ||
          i.id === product._id ||
          i.productId === product._id ||
          i.name === product.name
      ) || null;

    return match?.quantity || 0;
  };

  // ✅ Fix image URL for production:
  // If product.image is "/uploads/xxx.jpg", it should become `${API_BASE}/uploads/xxx.jpg`
  const resolveImageUrl = (imagePath) => {
    if (!imagePath) return "/placeholder.jpg";
    if (typeof imagePath !== "string") return "/placeholder.jpg";

    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    // if it starts with "/uploads/.."
    if (imagePath.startsWith("/")) {
      return `${API_BASE}${imagePath}`;
    }

    // if it is "uploads/.."
    return `${API_BASE}/${imagePath}`;
  };

  const renderSection = (key, title) => {
    const products = productsByCategory[key] || [];
    if (products.length === 0) return null;

    return (
      <section id={key} className="mb-20 scroll-mt-28">
        <h2 className="text-3xl font-bold mb-6 text-[#105132]">{title}</h2>

        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {products.map((product) => {
            const convertedPrice = (product.price * rate).toFixed(2);
            const symbol = symbolMap[currency] || "₹";
            const qty = getQtyInCart(product);
            const imgSrc = resolveImageUrl(product.image);

            return (
              <div
                key={product._id}
                className="relative bg-white p-5 rounded-2xl shadow hover:shadow-lg transition-all duration-200 border border-[#d4af57]/50 hover:scale-[1.02] flex flex-col"
              >
                <img
                  src={imgSrc}
                  alt={product.name}
                  className="h-40 w-full object-contain rounded mb-4"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.jpg";
                  }}
                />

                <h4 className="text-lg font-semibold mb-1 text-[#105132] text-center">
                  {product.name}
                </h4>

                <p className="text-sm text-gray-600 mb-3 text-center">
                  {symbol} {convertedPrice} {currency}
                </p>

                {!product.inStock && (
                  <span className="absolute top-2 right-2 bg-red-100 text-red-600 text-xs px-2 py-1 rounded font-semibold">
                    Out of Stock
                  </span>
                )}

                {/* ✅ Add to Cart OR Quantity Stepper */}
                <div className="mt-auto">
                  {qty === 0 ? (
                    <button
                      disabled={!product.inStock}
                      onClick={() =>
                        addToCart({
                          _id: product._id,
                          id: product._id,
                          productId: product._id,
                          name: product.name,
                          price: product.price,
                          image: product.image,
                          quantity: 1,
                        })
                      }
                      className={`w-full px-4 py-2 rounded text-white text-sm font-medium transition ${
                        product.inStock
                          ? "bg-[#236e47] hover:bg-[#1c4c39]"
                          : "bg-gray-400 cursor-not-allowed"
                      }`}
                    >
                      {product.inStock ? "Add to Cart" : "Unavailable"}
                    </button>
                  ) : (
                    <div className="flex items-center justify-between rounded-full border border-[#d4af57] bg-white px-3 py-2 shadow-sm">
                      <button
                        type="button"
                        aria-label="Decrease quantity"
                        onClick={() => decreaseQty(product.name)}
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-[#f3f0e4] active:scale-95 transition text-[#105132] font-bold"
                      >
                        −
                      </button>

                      <span className="min-w-[2rem] text-center font-semibold text-[#105132]">
                        {qty}
                      </span>

                      <button
                        type="button"
                        aria-label="Increase quantity"
                        onClick={() => increaseQty(product.name)}
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-[#f3f0e4] active:scale-95 transition text-[#105132] font-bold"
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="p-6 bg-[#f3f0e4] min-h-screen">
        <h1 className="text-4xl font-bold text-center mb-10 text-[#105132]">
          Our Products
        </h1>

        {[1, 2].map((section) => (
          <div key={section} className="mb-20">
            <div className="h-8 w-64 bg-gray-200 rounded mb-6 animate-pulse" />
            <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white p-5 rounded-2xl border border-gray-200 flex flex-col animate-pulse"
                >
                  <div className="h-40 w-full bg-gray-200 rounded mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                  <div className="h-8 bg-gray-200 rounded w-full mt-auto" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 bg-[#f3f0e4] min-h-screen text-[#105132]">
      <h1 className="text-4xl font-bold text-center mb-10">Our Products</h1>
      {renderSection("raw", "Raw Makhana")}
      {renderSection("roasted", "Roasted Makhana")}
      {renderSection("flavored", "Flavored Makhana")}
      {renderSection("giftpacks", "Gift Packs")}
    </div>
  );
}
